import { NovelItem } from '@app/_proto/Protos/novels';
import { GetActivePricingRotationsRequest_PricingTypeFilter } from '@app/_proto/Protos/pricing';
import { Any } from '@app/_proto/google/protobuf/any';
import { logSelectSeries } from '@app/analytics';
import { useMediaQuery } from '@app/domains/common/hooks/useMediaQuery';
import { getNovelPath } from '@app/domains/common/utils/path';
import HomeCollection from '@app/domains/home/components/HomeCollection';
import HomeContent from '@app/domains/home/components/HomeContent';
import HomeRotationTimeLeft from '@app/domains/home/components/HomeRotationTimeLeft';
import HomeSwiper from '@app/domains/home/components/HomeSwiper';
import { HOME_COLLECTION_TITLE_FONT_STYLE, HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE } from '@app/domains/home/styles';
import NovelResponsiveCoverImage from '@app/domains/novel/components/NovelResponsiveCoverImage';
import useNovelPricingRotations from '@app/domains/novel/hooks/useNovelPricingRotations';
import { timestampToDate } from '@app/libs/utils';
import { breakpoints } from '@app/utils/breakpoints';
import dayjs from 'dayjs';
import { orderBy } from 'lodash-es';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

export default function HomeRotationNovels() {
    const { data, isLoading } = useNovelPricingRotations({
        type: GetActivePricingRotationsRequest_PricingTypeFilter.Free,
    });

    const isDownLg = useMediaQuery(breakpoints.downLg);

    const items = useMemo(() => {
        if (!data) {
            return [];
        }

        return data.map(pricing => {
            const startDate = dayjs(timestampToDate(pricing.rotation?.startTime)).format('D MMM');
            const endDate = dayjs(timestampToDate(pricing.rotation?.endTime)).format('D MMM');

            return {
                novel: Any.unpack(pricing.object!, NovelItem)!,
                rotation: pricing.rotation!,
                timeRange: `${startDate} - ${endDate}`,
                isActive: dayjs().isBetween(
                    dayjs(timestampToDate(pricing.rotation?.startTime)),
                    dayjs(timestampToDate(pricing.rotation?.endTime))
                ),
            };
        });
    }, [data]);

    const handleClickSeries = (novel: NovelItem) => {
        logSelectSeries(novel);
    };

    if (!items.length) {
        return null;
    }

    return (
        <div className="relative left-[50%] right-[50%] -mr-[50vw] mt-10 -ml-[50vw] h-[540px] w-screen bg-[url('/images/novel-rotations-bg.png')] bg-center bg-cover bg-no-repeat">
            <div className="mx-auto max-w-[1280px] px-24 pt-[2rem]">
                <div className="relative">
                    <HomeCollection.Title
                        title="⚠️ Limited Time Special!"
                        classes={{ heading: `${HOME_COLLECTION_TITLE_FONT_STYLE} text-white dark:text-gray-t1` }}
                        isLoading={isLoading}
                    />
                </div>
                <HomeSwiper
                    slidesPerView="auto"
                    slidesPerGroup={isDownLg ? 2 : 4}
                    className={HOME_SWIPER_WITH_TOP_SHADOW_LAYOUT_STYLE}
                    isLoading={isLoading}
                >
                    {orderBy(
                        items,
                        [
                            b => b.isActive,
                            b =>
                                b.isActive
                                    ? timestampToDate(b.rotation.endTime)!.getDate()
                                    : timestampToDate(b.rotation.startTime)!.getDate(),
                        ],
                        ['desc', 'asc']
                    ).map(pricing => (
                        <HomeSwiper.Slide key={pricing.rotation.id} className="!w-208">
                            <HomeContent content={pricing.novel} isLoading={isLoading}>
                                <Link
                                    onClick={() => handleClickSeries(pricing.novel)}
                                    to={getNovelPath(pricing.novel.slug || '')}
                                >
                                    <div className="relative h-0 pb-[146%]">
                                        <NovelResponsiveCoverImage
                                            novel={pricing.novel}
                                            imgUrl={pricing.novel.coverUrl?.value}
                                            imgBlurHash={pricing.novel.coverBlurHash?.value}
                                            isLoading={isLoading}
                                            srcWidth={208}
                                            srcHeight={260}
                                            showRotation={false}
                                        >
                                            <div className="absolute bottom-[1rem] right-[15px] z-10">
                                                <HomeRotationTimeLeft
                                                    isActive={pricing.isActive}
                                                    date={
                                                        timestampToDate(
                                                            pricing.isActive
                                                                ? pricing.rotation.endTime
                                                                : pricing.rotation.startTime
                                                        )!
                                                    }
                                                />
                                            </div>
                                            <div className="absolute h-100 right-0 left-0 bottom-0 bg-gradient-to-t from-black to-transparent" />
                                        </NovelResponsiveCoverImage>
                                    </div>
                                </Link>
                                <HomeContent.Banner
                                    className={twMerge(
                                        'flex w-full items-center justify-center',
                                        pricing.isActive ? 'bg-[#00B000]' : 'bg-[#313131]'
                                    )}
                                    classes={{ text: 'uppercase text-[0.6rem] text-white' }}
                                    text={pricing.isActive ? 'Free Now' : 'Coming Soon'}
                                />
                                <HomeContent.TextWrapper className="mt-5">
                                    <HomeContent.ReviewScore className="font-set-b13 md:font-set-b16" />
                                    <HomeContent.Title className="font-set-sb13 text-white md:font-set-sb15" />
                                    <HomeContent.SubTitle
                                        className="text-[0.7rem]"
                                        subtitle={`Free ${pricing.timeRange}`}
                                    />
                                </HomeContent.TextWrapper>
                            </HomeContent>
                        </HomeSwiper.Slide>
                    ))}
                </HomeSwiper>
            </div>
        </div>
    );
}
